<template>
  <!-- Inline sync error banner (cached events still visible) -->
  <div v-if="type === 'banner'" class="status-banner error-banner">
    <div class="banner-content">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
      <span>Unable to sync with calendar server. Showing cached events.</span>
    </div>
    <button class="btn-retry-inline" :disabled="loading" @click="$emit('retry')">
      {{ loading ? 'Retrying...' : 'Retry Sync' }}
    </button>
  </div>

  <!-- Initial loading state (no cached events yet) -->
  <div v-else-if="type === 'loading'" class="status-message loading-state">
    <div class="spinner" />
    <span>Connecting to calendar service...</span>
  </div>

  <!-- Full-page error hero (API down, no cache) -->
  <div v-else-if="type === 'error'" class="error-hero-card">
    <div class="error-icon-wrapper">
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 1l22 22" /><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" /><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" /><path d="M10.71 5.05A16 16 0 0 1 22.58 9" /><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" /><path d="M8.53 16.11a6 6 0 0 1 6.95 0" /><line x1="12" y1="20" x2="12.01" y2="20" /></svg>
    </div>
    <h4>Calendar Service Unavailable</h4>
    <p>We couldn't connect to your calendar server. Please check your network or try reconnecting.</p>
    <button class="btn-retry-hero" :disabled="loading" @click="$emit('retry')">
      {{ loading ? 'Attempting Connection...' : 'Try Reconnecting' }}
    </button>
  </div>

  <!-- Empty state (no events) -->
  <div v-else-if="type === 'empty'" class="empty-state">
    <p>No upcoming events found.</p>
  </div>
</template>

<script setup>
defineProps({
  /** 'banner' | 'loading' | 'error' | 'empty' */
  type: {
    type: String,
    required: true,
    validator: (v) => ['banner', 'loading', 'error', 'empty'].includes(v),
  },
  loading: {
    type: Boolean,
    default: false
  },
});

defineEmits(['retry']);
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "@/assets/styles/abstract/variables" as *;

.status-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 1rem;
  border-radius: 10px;
  font-size: 0.85rem;
  margin-bottom: 1.25rem;

  &.error-banner {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
  }

  .banner-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .btn-retry-inline {
    background: #ffffff;
    border: 1px solid #fca5a5;
    color: #991b1b;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.25rem 0.6rem;
    border-radius: 6px;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #fee2e2;
    }

    &:disabled {
      opacity: 0.5;
    }
  }
}

.error-hero-card {
  background: #fef2f2;
  border: 1px dashed #fca5a5;
  border-radius: 14px;
  padding: 2.5rem 1.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin: 1rem 0;

  .error-icon-wrapper {
    background: #fee2e2;
    color: #dc2626;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  h4 {
    margin: 0;
    font-size: 1.1rem;
    color: #991b1b;
    font-weight: 700;
  }

  p {
    margin: 0;
    font-size: 0.88rem;
    color: #7f1d1d;
    max-width: 380px;
    line-height: 1.4;
  }

  .btn-retry-hero {
    margin-top: 0.5rem;
    background: #dc2626;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 0.6rem 1.2rem;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover:not(:disabled) {
      background: #b91c1c;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2.5rem 0;
  color: $grey-back;
  font-size: 0.9rem;

  .spinner {
    width: 18px;
    height: 18px;
    border: 2px solid color.mix($light-brown, #ffffff, 60%);
    border-top-color: $dark-blue;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

.empty-state {
  text-align: center;
  color: $grey-93;
  font-size: 0.95rem;
  padding: 2rem 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>